import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi, urlApiFunctionPlanning } from '../urls';

/**
 * Paso 1 del reproceso manual de GeoSort: dispara la descarga del reporte diario
 * de GeoSort (plataforma externa de Falabella) y su subida a Azure Blob Storage.
 * Vive en la Azure Function `af_funciones_diarias` (host productivo confirmado:
 * spread-funciones-diarias.azurewebsites.net), endpoint `api/geosort_diario`,
 * auth_level ANONYMOUS (no requiere function key) — ver
 * general_documents/documentacion/af_funciones_diarias/02-endpoints-y-rutas.md.
 * Síncrono: la respuesta solo llega cuando el CSV ya quedó subido a Blob Storage.
 * @param {Object} user - Usuario autenticado
 * @param {string} fecha - Fecha en formato 'yyyy-mm-dd'
 * @returns {Promise} Response con { success, fecha, message }
 */
export const runGeosortDiario = async (user, fecha) => {
  try {
    const config = basicAuthConfig(user);
    const baseUrl = (urlApiFunctionPlanning || '').replace(/\/$/, '');
    const endpoint = `${baseUrl}/api/geosort_diario?fecha=${encodeURIComponent(fecha)}`;

    const response = await axios.get(endpoint, config);

    return {
      success: true,
      fecha: response.data?.fecha || fecha,
      message: response.data?.message || 'Reporte de GeoSort descargado exitosamente'
    };
  } catch (error) {
    console.error('Error al descargar el reporte diario de GeoSort:', error);
    return {
      success: false,
      error: error.response?.data?.msg || error.response?.data?.message || 'Error al descargar el reporte diario de GeoSort'
    };
  }
};

/**
 * Paso 2 del reproceso manual de GeoSort: dispara el procesamiento bulk (asíncrono,
 * en background) del CSV ya descargado, para una fecha dada. Vive en `myspread-flask`,
 * blueprint `geoSort_bp`, `POST /geoSort/process-bulk-optimized` — ver
 * general_documents/documentacion/myspread-flask/otros-modulos/geosort.md.
 * La respuesta llega de inmediato con un total_guides estimado; el guardado real en
 * BD sigue corriendo en background varios minutos después de responder (fire-and-forget,
 * mismo patrón que la carga masiva de rutas — sin polling desde el navegador).
 * @param {Object} user - Usuario autenticado
 * @param {string} fecha - Fecha en formato 'yyyy-mm-dd'
 * @returns {Promise} Response con { success, fecha, totalGuides, message }
 */
export const runGeosortProcessBulk = async (user, fecha) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/geoSort/process-bulk-optimized`;

    const response = await axios.post(endpoint, { fecha }, config);

    return {
      success: response.data?.success ?? true,
      fecha: response.data?.fecha || fecha,
      totalGuides: response.data?.total_guides ?? 0,
      message: response.data?.message || 'Proceso de GeoSort iniciado exitosamente'
    };
  } catch (error) {
    console.error('Error al iniciar el procesamiento bulk de GeoSort:', error);
    const errorData = error.response?.data || {};
    return {
      success: false,
      error: errorData.message || errorData.error || 'Error al iniciar el procesamiento de GeoSort'
    };
  }
};
